import { useContext, useEffect, useState } from "react";
import Layout from "components/layout";
import { GlobalContext } from "components/stores/global-context";
import SiteBlocks from "components/blocks/site-blocks/site-blocks.js";
import { ThemeHelper, LocationHelper } from "components/helpers";
import BroadcastMessage, {
  handleBroadcastMessageStatus,
} from "components/block-elements/broadcast-message/broadcast-message";
import PageInOverlay from "./page-in-overlay";
import {
  extract,
  EventAggregator,
  CartContextConsumer,
  Modal,
  GhostButton,
  ModalPortal,
  useContentContainerRef,
  ICartContext,
  SessionStorageHelper,
  TextBlock,
  PrimaryButtonLink,
  ContentfulLivePreviewProvider,
  useGlobalBannerOverride,
  Footer,
} from "@parksandresorts/core";
import Upsell from "components/block-elements/upsell/upsell";
import SiteConfig from "components/stores/site-configuration-store";
import BottomInfoModal from "components/block-elements/bottom-info-modal/bottom-info-modal";
import JetpassSelection from "components/block-elements/jetpass-selection/jetpass-selection";
import styles from "./page.module.less";
import { ContentfulContentPageProps } from "templates/content-page";
import siteMetadata from "../../site-metadata";
import Header from "components/navigations/header/header";

type OverlayId = "first" | "second";

const Page = (props: ContentfulContentPageProps) => {
  const contentPage = props?.data.contentfulContentPage;
  const hideBroadcastMessage = contentPage?.hideBroadcastMessage ?? false;
  const pageTheme = contentPage?.pageTheme;
  const siteConfig = props?.data?.contentfulSiteConfiguration;
  const lockType = contentPage?.lockType;
  const lockValue = contentPage?.lockValue;
  const lockInputLabel = contentPage?.lockInputLabel;
  const lockInputPlaceholder = contentPage?.lockInputPlaceholder;
  const lockErrorMsg = contentPage?.lockErrorMessage;
  const lockNecessaryProducts = contentPage?.lockNecessaryProducts ?? [];
  const lockBlockingProducts = contentPage?.lockBlockingProducts ?? [];
  const lockMyPageMessageNotNecessaryProducts = contentPage?.lockMyPageMessageNotNecessaryProducts;
  const lockMyPageMessageBlockingProducts = contentPage?.lockMyPageMessageBlockingProducts;
  const lockMyPageMessageNotLoggedIn = contentPage?.lockMyPageMessageNotLoggedIn;

  const enableJetPassExplanation = siteConfig?.enableJetPassExplanation ?? true;
  const header = getHeader();
  const navbar = contentPage?.navbar;
  const context = useContext(GlobalContext);

  const locationState = props?.location?.state ?? undefined;
  const locationSearch = props?.location?.search ?? undefined;
  const blocks = extendBlocks(contentPage?.blocks ?? [], {
    locationState: locationState,
    pageTheme: pageTheme,
  });

  const siteName = siteMetadata.title;
  const pageTitle = contentPage?.title ?? "";
  const isStartPage = props?.pageContext?.slug === "/";
  const hasHeroTypeBlock = getHeroTypeBlock();

  const contentContainerRef = useContentContainerRef();
  const [activeOverlay, setActiveOverlay] = useState(null);

  function extendBlocks(
    // Incredibly lazy typing, but it's all I had energy for
    blocks: typeof contentPage.blocks,
    extendObject: { locationState: typeof locationState; pageTheme: typeof pageTheme },
  ) {
    if (!extendObject) {
      return blocks;
    }

    const extendedBlocks = blocks.map((block) => {
      return Object.assign({}, extendObject, block);
    });

    return extendedBlocks;
  }

  useEffect(() => {
    if (activeOverlay) {
      const newActiveOverlay = context.overlay.activeOverlays?.length > 0 ? context.overlay.activeOverlays[0] : [];
      setActiveOverlay(newActiveOverlay as any);
    }
  }, [activeOverlay]);

  function getHeader() {
    const header = contentPage.pageHeader instanceof Object ? contentPage.pageHeader : siteConfig.defaultSiteHeader;
    return header;
  }

  function onClickCloseOverlay(overlayId: OverlayId) {
    const newOverlay = {
      ...context.overlay,
      [overlayId]: {
        ...context.overlay[overlayId],
        animateCloseOverlay: true,
      },
    };

    context.setValue({
      overlay: newOverlay,
    });
  }

  function pageInOverlayOnAnimationEnd(overlayId: OverlayId) {
    const activeOverlay = context.overlay[overlayId];
    if (activeOverlay.isOpen && activeOverlay.animateCloseOverlay) {
      const activeOverlays = [...context.overlay.activeOverlays];
      activeOverlays.splice(0, 1);
      const newOverlay = {
        ...context.overlay,
        activeOverlays: activeOverlays,
        [overlayId]: {
          isOpen: false,
          animateCloseOverlay: false,
          props: null,
        },
      };
      context.setValue(
        {
          overlay: newOverlay,
        },
        () => {
          EventAggregator.publish("trackPageview", {
            virtualPagePath: window.history.state.previousPath,
            virtualPageTitle: pageTitle,
          });

          if (activeOverlays.length === 1) {
            const remainingOverlay = activeOverlays[0];
            const remainingOverlayObject = context.overlay[remainingOverlay] as any;
            const to = remainingOverlayObject.to;
            const title = extract(remainingOverlayObject, "contentfulContentPage.title");
            LocationHelper.updateHistory(to, title, true);
          } else {
            LocationHelper.updateHistory(props.pageContext.slug, pageTitle);
          }
        },
      );
    }
  }

  const emptyCartContextValue = (cartContext: ICartContext) => {
    cartContext.setValue({ showJetPassModal: false, jetPassAlternativeOptOut: null, guests: null, jetPassOptions: {} });
  };

  function informationModalOnAnimationEnd() {
    if (context.showInformationModal && context.animateCloseInformationModal) {
      context.setValue({
        showInformationModal: false,
        informationModalContent: { headline: null, productName: null, body: null },
        animateCloseInformationModal: false,
      });
    }
  }

  function getHeroTypeBlock() {
    const heroTypeBlocks = ["ContentfulHeroBlock", "ContentfulUpcomingBlock", "ContentfulMyPageBlock"];
    let hasHeroTypeBlock = false;
    if (blocks instanceof Array && blocks.length > 0) {
      const firstBlock = blocks[0];
      hasHeroTypeBlock = firstBlock ? heroTypeBlocks.some((hero) => firstBlock.__typename === hero) : false;
    }
    return hasHeroTypeBlock;
  }

  // This is a bogus string, but it includes the string "pej.se" which causes the app to open it in the native web browser instead of in the webview
  // It's probably not necessary to hide it inside this bogus string, but why not?
  const openInBrowserMagicString = `go${"pej.se"}jcz.dfyak.ldhaf.kboed`;
  const isInApp = !!SessionStorageHelper.getItem("isWebView");

  const bannerProps = useGlobalBannerOverride(
    siteConfig && {
      broadcastMessageContent: siteConfig.broadcastMessageContent,
      broadcastMessageStatus: siteConfig.broadcastMessageStatus as "General info" | "Important info" | "Critical info",
      broadcastMessageLink: siteConfig.broadcastMessageLink,
    },
    hideBroadcastMessage !== true,
    props?.data.contentfulContentPage.node_locale as "sv" | "en",
  );

  const isShowingTopBanner = bannerProps && handleBroadcastMessageStatus(bannerProps).shouldShow;

  return (
    <>
      <ContentfulLivePreviewProvider locale={props?.data.contentfulContentPage.node_locale}>
        {isInApp && (
          <>
            <div className={styles.fullView}>
              <video preload="auto" loop playsInline muted autoPlay className={styles.backgroundVideo}>
                <source
                  src="https://videos.ctfassets.net/49ktgogijq8a/4RWevspr3krjigVk6Aop21/95f3e0d7a3d26906ec796fdccf7c6e9b/SKARA_2020_UTAN_TEXT_MED_SPEAKER_4444.mp4"
                  type="video/mp4"
                />
              </video>
            </div>
            <div className={`${styles.fullView} ${styles.textContainer} sommarland-primary`}>
              <TextBlock
                title={SiteConfig.getLabel("DISABLE_APP_TITLE", "Vi har stängt ner appen!")}
                text={{
                  childMarkdownRemark: {
                    html: `<p>${SiteConfig.getLabel(
                      "DISABLE_APP_TEXT",
                      "Besök istället vår hemsida för aktuell information. Vi ses i parken!",
                    )}</p>`,
                  },
                }}
                callToActionButton={{
                  title: SiteConfig.getLabel("DISABLE_APP_LINK", "Till hemsidan"),
                  externalUrl: `/shut-down-app-redirect#${openInBrowserMagicString}`,
                  openInNewTab: true,
                }}
                centerText={true}
                backgroundColor={true}
              />
              <PrimaryButtonLink
                iconLeftNew="flag-en"
                iconSize="m"
                className="sommarland-tertiary"
                label="English"
                url={`/en/shut-down-app-redirect#${openInBrowserMagicString}`}
                openInNewTab
              />
            </div>
          </>
        )}
        {!isInApp && (
          <div
            id="theme-wrapper"
            className={"site-theme-" + ThemeHelper.getTheme()}
            data-contentful-id={contentPage.contentful_id}
          >
            <div className={pageTheme ? `page-theme-${pageTheme.toLowerCase()}` : "page-theme-none"}>
              <Layout key="page-layout">
                {isShowingTopBanner && (
                  <div id="BroadcastMessage">
                    <BroadcastMessage {...bannerProps} />
                  </div>
                )}
                {header instanceof Object && !context.isTopNavigationHidden && (
                  <Header
                    siteName={siteName}
                    isStartPage={isStartPage}
                    pageHeader={header}
                    siteConfig={siteConfig}
                    prefillHeader={!hasHeroTypeBlock}
                    isShowingTopBanner={!!isShowingTopBanner}
                  />
                )}
                <main id="Main" className={context.isMainVisible === false ? "display-none" : ""}>
                  {blocks instanceof Array && blocks.length && (
                    <SiteBlocks
                      blocks={blocks}
                      hasHero={hasHeroTypeBlock}
                      isShowingTopBanner={!!isShowingTopBanner}
                      navbar={navbar}
                      /** For lock HOC */
                      lockType={lockType}
                      lockValue={lockValue}
                      lockInputLabel={lockInputLabel}
                      lockInputPlaceholder={lockInputPlaceholder}
                      lockErrorMsg={lockErrorMsg}
                      lockNecessaryProducts={lockNecessaryProducts}
                      lockBlockingProducts={lockBlockingProducts}
                      lockMyPageMessageNotNecessaryProducts={lockMyPageMessageNotNecessaryProducts}
                      lockMyPageMessageBlockingProducts={lockMyPageMessageBlockingProducts}
                      lockMyPageMessageNotLoggedIn={lockMyPageMessageNotLoggedIn}
                      setIsViewportScrollable={(value: boolean) => context.setValue({ isViewportScrollable: value })}
                      locationSearch={locationSearch}
                    />
                  )}
                </main>
                {siteConfig && !context.isFooterHidden && (
                  <footer id="Footer" className={[context.isMainVisible ? "" : "display-none"].join(" ")}>
                    <Footer siteConfig={siteConfig} />
                  </footer>
                )}
              </Layout>
            </div>
          </div>
        )}
      </ContentfulLivePreviewProvider>

      <CartContextConsumer>
        {(cartContext) =>
          cartContext.showJetPassModal &&
          contentContainerRef && (
            <Modal
              className={["jetpass-modal", pageTheme ? `page-theme page-theme-${pageTheme.toLowerCase()}` : ""].join(
                " ",
              )}
              onClose={() => emptyCartContextValue(cartContext)}
              refWrapperElement={contentContainerRef}
              modalTitle="JetPass"
              isOpen={cartContext.showJetPassModal}
              disableScrollClassName="overflow-hidden"
            >
              <JetpassSelection
                disableIntroStep={!enableJetPassExplanation}
                globalContext={context}
                cartContext={cartContext}
                jetPassDate={cartContext.jetPassDate}
                alternativeOptOut={cartContext.jetPassAlternativeOptOut}
                jetPassOptions={cartContext.jetPassOptions}
                guests={cartContext.guests}
                onDoneAddToCart={cartContext.onDoneAddToCart}
                theme={ThemeHelper.getTheme()}
              />
            </Modal>
          )
        }
      </CartContextConsumer>
      {contentContainerRef && (
        <>
          <Modal
            disableTouchLock={true}
            className={[
              "bottom-info-modal",
              context.animateCloseInformationModal ? "bottom-info-modal--closing" : "",
              pageTheme ? `page-theme page-theme-${pageTheme.toLowerCase()}` : "",
            ].join(" ")}
            modalTitle="Information"
            refWrapperElement={contentContainerRef}
            isOpen={context.showInformationModal}
            disableInitialFocusInModal={true}
            onClose={() => context.setValue({ animateCloseInformationModal: true })}
            overlayOnAnimationEnd={() => informationModalOnAnimationEnd()}
          >
            <BottomInfoModal
              headline={context.informationModalContent.headline}
              productName={context.informationModalContent.productName}
              body={context.informationModalContent.body}
            />
          </Modal>

          <Modal
            className={["upsell-modal"].join(" ")}
            modalTitle="Upsell"
            refWrapperElement={contentContainerRef}
            isOpen={context.showUpsellModal}
            disableScrollClassName="overflow-hidden"
            onClose={() => context.setValue({ showUpsellModal: false })}
          >
            <Upsell upsellType="UPGRADE_PRODUCT" upgradeInfo={{ upgradeFrom: 222, upgradeTo: 111, quantity: 1 }} />
          </Modal>

          <Modal
            className={[
              pageTheme ? `page-theme page-theme-${pageTheme.toLowerCase()}` : "",
              "page-overlay",
              activeOverlay === "first" ? "page-overlay--active" : "",
              context.overlay.first.animateCloseOverlay ? "page-overlay--closing" : "",
            ].join(" ")}
            modalTitle="Page overlay"
            refWrapperElement={contentContainerRef}
            isOpen={context.overlay.first.isOpen}
            onClose={() => onClickCloseOverlay("first")}
            overlayOnAnimationEnd={() => pageInOverlayOnAnimationEnd("first")}
            disableScrollClassName="overflow-hidden-first"
          >
            <PageInOverlay {...context.overlay.first.props}>
              <div className="close-overlay-wrapper">
                <GhostButton
                  className={["waves-effect"].join(" ")}
                  onClick={() => onClickCloseOverlay("first")}
                  iconCenter="icon-close"
                />
              </div>
            </PageInOverlay>
          </Modal>

          <Modal
            className={[
              pageTheme ? `page-theme page-theme-${pageTheme.toLowerCase()}` : "",
              "page-overlay",
              activeOverlay === "second" ? "page-overlay--active" : "",
              context.overlay.second.animateCloseOverlay ? "page-overlay--closing" : "",
            ].join(" ")}
            modalTitle="Page overlay"
            refWrapperElement={contentContainerRef}
            isOpen={context.overlay.second.isOpen}
            onClose={() => onClickCloseOverlay("second")}
            overlayOnAnimationEnd={() => pageInOverlayOnAnimationEnd("second")}
            disableScrollClassName="overflow-hidden-second"
          >
            <PageInOverlay {...context.overlay.second.props}>
              <div className="close-overlay-wrapper">
                <GhostButton
                  className={["waves-effect"].join(" ")}
                  onClick={() => onClickCloseOverlay("second")}
                  iconCenter="icon-close"
                />
              </div>
            </PageInOverlay>
          </Modal>
        </>
      )}
      <ModalPortal pageTheme={`${pageTheme} ${pageTheme}`} />
    </>
  );
};

export default Page;
